import { StateReset } from 'ngxs-reset-plugin';
import { debounceTime, distinctUntilChanged, shareReplay } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { SpinnerService, StateSpinnerService } from '@mitsubishi/ui/spinner';
import { getStateActionErroredFactory, getStateActionResultFactory } from '@mitsubishi/utils';

import { hasActionsExecuting } from '@ngxs-labs/actions-executing';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, Store } from '@ngxs/store';

import { ProjectActions } from '../project/project.actions';
import { DatasheetActions } from './datasheet.actions';
import { DatasheetSelectors } from './datasheet.selectors';
import { DatasheetState } from './datasheet.state';

@Injectable({
  providedIn: 'root',
})
export class DatasheetFacade extends StateSpinnerService {
  private onActionErrored = getStateActionErroredFactory();
  private onActionResult = getStateActionResultFactory();

  datasheet$ = this.store.select(DatasheetSelectors.getDatasheet);
  messages$ = this.store.select(DatasheetSelectors.getMessages);
  fields$ = this.store.select(DatasheetSelectors.getSectionTables);
  selectedGroup$ = this.store.select(DatasheetSelectors.getSelectedGroup);
  selectedSection$ = this.store.select(DatasheetSelectors.getSelectedSection);
  selectedRow$ = this.store.select(DatasheetSelectors.getSelectedRow);
  isEditing$ = this.store
    .select(DatasheetSelectors.getIsEditing)
    .pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));
  form$ = this.store.select(DatasheetSelectors.getForm);
  somethingEdited$ = this.store.select(DatasheetSelectors.getSomethingEdited);
  isDatasheetLoading$ = this.store
    .select(hasActionsExecuting([DatasheetActions.LoadDatasheet, DatasheetActions.CalculateDatasheet]))
    .pipe(debounceTime(150), distinctUntilChanged());

  constructor(spinnerService: SpinnerService, store: Store, actions$: Actions) {
    super(store, spinnerService, actions$);
    this.addSpinnerActions([ProjectActions.Load, DatasheetActions.CalculateDatasheet, DatasheetActions.SaveDatasheet]);
    this.addSpinnerActions(
      [
        DatasheetActions.LoadDatasheet,
        DatasheetActions.DownloadDatasheetRequestLog,
        DatasheetActions.UpdateSectionPdfVisibility,
        DatasheetActions.DownloadPartialLoads,
      ],
      true
    );
  }

  @Dispatch()
  calculateDatasheet = () => new DatasheetActions.CalculateDatasheet();

  @Dispatch()
  loadDatasheet = (sendEditedTables: boolean) => new DatasheetActions.LoadDatasheet(sendEditedTables);

  @Dispatch()
  saveDatasheet = () => new DatasheetActions.SaveDatasheet();

  @Dispatch()
  setSelectedGroupAndSection = (groupId: number, sectionId: string) =>
    new DatasheetActions.SetSelectedGroupAndSection(groupId, sectionId);

  @Dispatch()
  setSelectedRow = (rowKey?: string) => new DatasheetActions.SetSelectedRow(rowKey);

  @Dispatch()
  clearState = () => new DatasheetActions.ClearState();

  @Dispatch()
  downloadDatasheetRequestLog = () => new DatasheetActions.DownloadDatasheetRequestLog();
  @Dispatch()
  downloadSelectionDatasheetPdfRequestLog = () => new DatasheetActions.DownloadSelectionDatasheetPdfRequestLog();
  @Dispatch()
  downloadStudyDatasheetPdfRequestLog = () => new DatasheetActions.DownloadStudyDatasheetPdfRequestLog();

  updateSectionPdfVisibility = (datasheetNotes: string, sections: string[]) => {
    this.store.dispatch(new DatasheetActions.UpdateSectionPdfVisibility(datasheetNotes, sections));

    return this.onActionResult(DatasheetActions.UpdateSectionPdfVisibility);
  };

  datasheetCreateStudyUnitPdf(studySubcategoryId: string, requestTraceId?: string) {
    this.store.dispatch(new DatasheetActions.CreateStudyUnitPdf(studySubcategoryId, requestTraceId));

    return this.onActionErrored(DatasheetActions.CreateStudyUnitPdf);
  }

  datasheetCreateEaUnitPdf(requestTraceId?: string) {
    this.store.dispatch(new DatasheetActions.CreateEaUnitPdf(requestTraceId));

    return this.onActionErrored(DatasheetActions.CreateEaUnitPdf);
  }

  @Dispatch()
  setIsEditing = (isEditing: boolean) => new DatasheetActions.SetIsEditing(isEditing);

  @Dispatch()
  setEditedValues = (values: Record<string, unknown>) => new DatasheetActions.SetEditedValues(values);

  @Dispatch()
  downloadPartialLoads = () => new DatasheetActions.DownloadPartialLoads();

  @Dispatch()
  resetState() {
    return new StateReset(DatasheetState);
  }
}
